/**
 * One on-screen slide from a beginner-course lesson, as lesson notes:
 * a title with bullets, a table, a flow ("Disclosure → Claims → …"), a
 * claim broken into labelled parts, and a footnote. Only the parts a
 * slide has are drawn.
 */
import React from 'react';
import type { CourseSlide } from '@/api/client';

function ClaimParts({ parts }: { parts: NonNullable<CourseSlide['claim']> }) {
  return (
    <p className="whitespace-pre-wrap rounded-sm border border-zinc-200 bg-zinc-50 px-3 py-2 font-serif text-[13px] leading-6 text-zinc-800">
      {parts.map((part, i) => {
        if (typeof part === 'string') {
          // eslint-disable-next-line react/no-array-index-key
          return <React.Fragment key={i}>{part}</React.Fragment>;
        }
        const [text, label] = part;
        return (
          // eslint-disable-next-line react/no-array-index-key
          <span key={i} className="rounded-sm bg-[#002FA7]/5 px-0.5" title={label}>
            <span className="border-b border-[#002FA7]/40">{text}</span>
            <sup className="ml-0.5 font-sans text-[10px] font-medium text-[#002FA7]">{label}</sup>
          </span>
        );
      })}
    </p>
  );
}

export default function SlideCard({ slide }: { slide: CourseSlide }) {
  const rows = slide.table?.rows || [];
  return (
    <div className="rounded-sm border border-zinc-200 bg-white px-3 py-2.5 text-sm" data-testid="course-slide">
      {slide.title && <div className="mb-1.5 font-medium text-zinc-900">{slide.title}</div>}
      {slide.flow && slide.flow.length > 0 && (
        <ol className="mb-1.5 flex flex-wrap items-center gap-1 text-xs" aria-label="Steps">
          {slide.flow.map((step, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <li key={i} className="flex items-center gap-1">
              {i > 0 && <span aria-hidden className="text-zinc-400">→</span>}
              <span className="rounded-sm border border-zinc-200 bg-zinc-50 px-1.5 py-0.5 text-zinc-700">{step}</span>
            </li>
          ))}
        </ol>
      )}
      {slide.bullets && slide.bullets.length > 0 && (
        <ul className="mb-1.5 list-disc space-y-0.5 pl-5 text-zinc-700">
          {slide.bullets.map((b, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <li key={i}>{b}</li>
          ))}
        </ul>
      )}
      {rows.length > 0 && (
        <div className="mb-1.5 overflow-x-auto">
          <table className="w-full border-collapse text-xs">
            {slide.table?.head && slide.table.head.length > 0 && (
              <thead>
                <tr>
                  {slide.table.head.map((h, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <th key={i} className="border-b border-zinc-200 bg-zinc-50 px-2 py-1 text-left font-medium text-zinc-700">{h}</th>
                  ))}
                </tr>
              </thead>
            )}
            <tbody>
              {rows.map((row, r) => (
                // eslint-disable-next-line react/no-array-index-key
                <tr key={r}>
                  {row.map((cell, c) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <td key={c} className="border-b border-zinc-100 px-2 py-1 align-top text-zinc-700">{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {slide.claim && slide.claim.length > 0 && <ClaimParts parts={slide.claim} />}
      {slide.note && <p className="mt-1 text-xs text-zinc-500">{slide.note}</p>}
    </div>
  );
}
